import { Moment } from 'moment';
import { formatMomentToValueDate } from './formatMomentToValueDate';
import { formatValueDateToMoment } from './formatValueDateToMoment';
import { useEffect } from 'react';
import { values } from '@morf/proto/values/v1/values_ts_proto';

export type useEarliestHighlightedDateProps = {
  availableDates: values.v1.Date[];
  beginningOfMonth: values.v1.Date;
  handleNextMonthClick: () => void;
  hasAvailabilityEverBeenLoaded: boolean;
  isAutoSelectedDate: boolean;
  isLoading: boolean;
  now: Moment;
  nrOfMonthsToAutoAdvance: number;
  selectedDate: values.v1.Date | undefined;
  setHasAvailabilityEverBeenLoaded?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setSelectedDate: React.Dispatch<
    React.SetStateAction<values.v1.Date | undefined>
  >;
};

export const useEarliestHighlightedDate = ({
  availableDates,
  beginningOfMonth,
  handleNextMonthClick,
  hasAvailabilityEverBeenLoaded,
  isAutoSelectedDate,
  isLoading,
  now,
  nrOfMonthsToAutoAdvance,
  selectedDate,
  setHasAvailabilityEverBeenLoaded,
  setSelectedDate,
}: useEarliestHighlightedDateProps): void => {
  const beginningOfMonthMoment = formatValueDateToMoment(beginningOfMonth);

  useEffect(() => {
    if (availableDates.length > 0) {
      // Mark that availability has been loaded at least once
      if (!hasAvailabilityEverBeenLoaded && setHasAvailabilityEverBeenLoaded) {
        setHasAvailabilityEverBeenLoaded(true);
      }

      if (isAutoSelectedDate) {
        let earliestMoment: Moment | undefined = undefined;

        for (const date of availableDates) {
          const currentMoment = formatValueDateToMoment(date);

          if (
            currentMoment.isSame(beginningOfMonthMoment, 'month') &&
            (!earliestMoment || currentMoment.isBefore(earliestMoment))
          ) {
            earliestMoment = currentMoment;
          }
        }

        if (earliestMoment) {
          const earliestDate = formatMomentToValueDate(earliestMoment);

          if (!selectedDate || selectedDate.day !== earliestDate.day) {
            setSelectedDate(earliestDate);
          }
        }
      }
    } else if (!isLoading && !hasAvailabilityEverBeenLoaded) {
      // Only auto-advance if availability has NEVER been loaded
      // This prevents auto-advance after the user has already seen availability
      const maxAllowedDate = now.add(nrOfMonthsToAutoAdvance, 'months');
      const isWithinThemeLimit = beginningOfMonthMoment.isSameOrBefore(
        maxAllowedDate,
        'month'
      );

      if (isWithinThemeLimit) {
        handleNextMonthClick();
      } else if (setHasAvailabilityEverBeenLoaded) {
        // Auto-advance limit reached with no availability found.
        // Prevent further auto-advance when user navigates back.
        setHasAvailabilityEverBeenLoaded(true);
      }
    }
  }, [
    availableDates,
    beginningOfMonth,
    hasAvailabilityEverBeenLoaded,
    isLoading,
    now,
    nrOfMonthsToAutoAdvance,
    selectedDate,
    setHasAvailabilityEverBeenLoaded,
  ]);
};
